/**
 * Excel helper tools for finding files and inspecting their structure
 */

import { z } from "zod";
import type { GraphApi, DriveItem, ExcelTable } from "../api/graph-api.js";
import { logger } from "../utils/logger.js";

// ============================================================================
// Search Excel Files Tool
// ============================================================================

export const searchExcelFilesInputSchema = z.object({
  query: z.string().describe("File name or search term (e.g., '365 Schedule' or 'Budget.xlsx')"),
});

export type SearchExcelFilesInput = z.infer<typeof searchExcelFilesInputSchema>;

export const searchExcelFilesTool = {
  name: "search_excel_files",
  description:
    "Search for Excel files in OneDrive by name. Returns matching files with their IDs and paths.",
  inputSchema: {
    type: "object" as const,
    properties: {
      query: {
        type: "string",
        description: "File name or search term",
      },
    },
    required: ["query"],
  },
};

export interface ExcelFileInfo {
  id: string;
  name: string;
  path: string;
  driveId: string;
  webUrl?: string;
}

export async function handleSearchExcelFiles(
  graphApi: GraphApi,
  input: unknown
): Promise<string> {
  const parsed = searchExcelFilesInputSchema.parse(input);

  logger.info({ query: parsed.query }, "Searching for Excel files");

  let excelFiles: DriveItem[] = [];

  // Try search first
  try {
    const results = await graphApi.searchFiles(parsed.query);
    excelFiles = results.filter(
      (item) =>
        item.file &&
        (item.name.endsWith(".xlsx") ||
          item.name.endsWith(".xls") ||
          item.name.endsWith(".xlsm"))
    );
  } catch (searchError) {
    logger.warn({ searchError }, "Search failed, trying path-based lookup");
  }

  // If search didn't find anything and query looks like a path or filename, try direct path
  if (excelFiles.length === 0) {
    // Try common paths
    const pathsToTry = [
      parsed.query,
      `/${parsed.query}`,
      `/Attachments/${parsed.query}`,
      `/Documents/${parsed.query}`,
      `/Documents/Attachments/${parsed.query}`,
    ];

    // Add .xlsx if not present
    const expandedPaths = pathsToTry.flatMap((p) =>
      p.endsWith(".xlsx") || p.endsWith(".xls") ? [p] : [p, `${p}.xlsx`]
    );

    for (const path of expandedPaths) {
      try {
        const file = await graphApi.getFileByPath(path);
        if (file && file.file) {
          excelFiles.push(file);
          break;
        }
      } catch {
        // Path not found, continue trying
      }
    }
  }

  if (excelFiles.length === 0) {
    return `No Excel files found for "${parsed.query}". Check the file name or provide full path.`;
  }

  const lines: string[] = [];
  for (const file of excelFiles) {
    lines.push(`${file.name} - ID: ${file.id}, Drive: ${file.parentReference?.driveId || "default"}`);
  }
  return `Found ${excelFiles.length} file(s):\n${lines.join("\n")}`;
}

// ============================================================================
// Inspect Excel File Tool
// ============================================================================

export const inspectExcelFileInputSchema = z.object({
  fileId: z.string().describe("The file ID from search_excel_files"),
  driveId: z.string().optional().describe("The drive ID (uses default OneDrive if omitted)"),
});

export type InspectExcelFileInput = z.infer<typeof inspectExcelFileInputSchema>;

export const inspectExcelFileTool = {
  name: "inspect_excel_file",
  description:
    "Inspect an Excel file to find tables and columns. Use search_excel_files first to find the file ID.",
  inputSchema: {
    type: "object" as const,
    properties: {
      fileId: {
        type: "string",
        description: "The file ID from search_excel_files",
      },
      driveId: {
        type: "string",
        description: "The drive ID (uses default OneDrive if omitted)",
      },
    },
    required: ["fileId"],
  },
};

export interface ExcelFileMetadata {
  fileId: string;
  driveId: string;
  tables: Array<{
    id: string;
    name: string;
    columns: Array<{ name: string; index: number }>;
  }>;
  worksheets: Array<{ name: string; position: number }>;
}

export async function handleInspectExcelFile(
  graphApi: GraphApi,
  input: unknown
): Promise<string> {
  const parsed = inspectExcelFileInputSchema.parse(input);

  logger.info({ fileId: parsed.fileId }, "Inspecting Excel file");

  // Get drive ID if not provided
  const driveId = parsed.driveId || (await graphApi.getMyDriveId());

  // Get tables
  let tables: ExcelTable[] = [];
  try {
    tables = await graphApi.getExcelTables(driveId, parsed.fileId);
  } catch (error) {
    logger.warn({ error }, "Could not get Excel tables");
  }

  // Get worksheets
  let worksheets: Array<{ name: string; position: number }> = [];
  try {
    const wsData = await graphApi.getExcelWorksheets(driveId, parsed.fileId);
    worksheets = wsData.map((ws) => ({ name: ws.name, position: ws.position }));
  } catch (error) {
    logger.warn({ error }, "Could not get Excel worksheets");
  }

  // Build concise output
  const wsNames = worksheets.map((ws) => ws.name).join(", ");

  if (tables.length > 0) {
    const tableInfo: string[] = [];
    for (const table of tables) {
      try {
        const columns = await graphApi.getExcelTableColumns(driveId, parsed.fileId, table.id);
        tableInfo.push(`${table.name}: ${columns.map((c) => c.name).join(", ")}`);
      } catch {
        tableInfo.push(`${table.name}`);
      }
    }
    return `Worksheets: ${wsNames}\nTables: ${tableInfo.join("; ")}\n\nDrive: ${driveId}, File: ${parsed.fileId}, Table: ${tables[0]!.name}`;
  }

  // No tables - check for headers
  let headers = "";
  if (worksheets.length > 0) {
    try {
      const range = await graphApi.getExcelUsedRange(driveId, parsed.fileId, worksheets[0]!.name);
      if (range.values && range.values.length > 0) {
        headers = (range.values[0] as string[]).filter((h) => h).join(", ");
      }
    } catch { /* ignore */ }
  }

  return `Worksheets: ${wsNames}\nNo tables found. Convert data to Table in Excel first (Ctrl+T).\n${headers ? `Detected headers: ${headers}` : ""}`;
}

// ============================================================================
// Get Excel File Info (for build_flow integration)
// ============================================================================

export async function getExcelFileInfo(
  graphApi: GraphApi,
  fileName: string
): Promise<{
  found: boolean;
  file?: ExcelFileInfo;
  tables?: Array<{ name: string; columns: string[] }>;
  error?: string;
}> {
  try {
    // Search for the file
    const results = await graphApi.searchFiles(fileName);
    const excelFiles = results.filter(
      (item) =>
        item.file &&
        (item.name.endsWith(".xlsx") ||
          item.name.endsWith(".xls") ||
          item.name.endsWith(".xlsm"))
    );

    if (excelFiles.length === 0) {
      return { found: false, error: `No Excel file found matching "${fileName}"` };
    }

    const file = excelFiles[0]!;
    const driveId = file.parentReference?.driveId || (await graphApi.getMyDriveId());

    const fileInfo: ExcelFileInfo = {
      id: file.id,
      name: file.name,
      path: file.parentReference?.path?.replace("/drive/root:", "") || "",
      driveId,
      webUrl: file.webUrl,
    };

    // Get tables
    const tables = await graphApi.getExcelTables(driveId, file.id);

    const tableInfo: Array<{ name: string; columns: string[] }> = [];

    for (const table of tables) {
      try {
        const columns = await graphApi.getExcelTableColumns(driveId, file.id, table.id);
        tableInfo.push({
          name: table.name,
          columns: columns.map((c) => c.name),
        });
      } catch {
        tableInfo.push({ name: table.name, columns: [] });
      }
    }

    return { found: true, file: fileInfo, tables: tableInfo };
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error);
    return { found: false, error: msg };
  }
}
